import { ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

interface StatCardProps {
  /** Stat label */
  label: string;
  /** Primary value to display */
  value: string | number;
  /** Optional icon */
  icon?: ReactNode;
  /** Optional trend indicator: 'up' | 'down' | 'neutral' */
  trend?: 'up' | 'down' | 'neutral';
  /** Optional trend value (e.g., "+12%") */
  trendValue?: string;
  /** Optional trend label (e.g., "vs last week") */
  trendLabel?: string;
  /** Optional description below value */
  description?: string;
}

export function StatCard({
  label,
  value,
  icon,
  trend,
  trendValue,
  trendLabel,
  description
}: StatCardProps) {
  const trendColors = {
    up: 'text-green-500',
    down: 'text-red-500',
    neutral: 'text-muted-foreground'
  };

  const TrendIcon = {
    up: TrendingUp,
    down: TrendingDown,
    neutral: Minus
  }[trend || 'neutral'];

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">{label}</p>
            <p className="text-3xl font-bold text-foreground mt-1">{value}</p>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          {icon && (
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              {icon}
            </div>
          )}
        </div>
        
        {trend && trendValue && (
          <div className="flex items-center gap-1 mt-3 text-sm">
            <TrendIcon className={cn("h-4 w-4", trendColors[trend])} />
            <span className={cn("font-medium", trendColors[trend])}>
              {trendValue}
            </span>
            {trendLabel && (
              <span className="text-muted-foreground">{trendLabel}</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
